import Box from 'assets/components/box/Box';
import Button from 'assets/components/button/Button';
import LinkButton from 'assets/components/button/LinkButton';
import ThemeButtonCircle from 'assets/components/button/themes/Theme.Button.Circle';
import DataMap from 'assets/components/dataMap/DataMap';
import FormatValue from 'assets/components/formatValue/FormatValue';
import Icon from 'assets/components/icon/Icon';
import Page from 'assets/components/page/Page';
import ThemePageError from 'assets/components/page/themes/Theme.Page.Error';
import ThemePageList from 'assets/components/page/themes/Theme.Page.List';
import Pagination from 'assets/components/pagination/Pagination';
import ThemePaginationRounded from 'assets/components/pagination/themes/Theme.Pagination.Rounded';
import ThemeTableSimple from 'assets/components/table/themes/Theme.Table.Simple';
import ThemeTagRounded from 'assets/components/tag/themes/Theme.Tag.Rounded';
import { getLocales } from 'assets/locales/Locale';
import Theme from 'assets/themes/Theme.Common';
import { defaultDateTimeFormat, localizeDate } from 'assets/utils/data/Date';
import React from 'react';
import { isDesktop, isMobile } from 'react-device-detect';
import {
  ClaimJobPaymentAction,
  FinanceClaimJobPaymentInfoTab,
  useClaimJobPaymentProvider,
} from '../Finance.ClaimJobPayment.Hooks';
import ClaimJobPaymentFilters from './Finance.ClaimJobPayment.Filters';
import ClaimJobPaymentInfo from './Finance.ClaimJobPayment.Info';
import ClaimJobPaymentActions from './Finance.ClaimJobPayment.Actions';
import ThemeWrapperStack from 'assets/themes/wrappers/Theme.Wrapper.Stack';

export default function ClaimJobPaymentList() {
  const { lang, translate } = getLocales();
  const { listApi, pageRouter, permissions } = useClaimJobPaymentProvider();

  return !listApi.payload && !listApi.isExecuting && listApi.apiCounter > 0 ? (
    <Page
      class={ThemePageError}
      sidebar={<Icon class="fas-clipboard-list" />}
      header={lang.claimsPaymentHistory}
      render={lang.noInfo}
    />
  ) : (
    <Page
      class={ThemePageList}
      sidebar={<ClaimJobPaymentInfo />}
      isLoading={listApi.isExecuting}
      header={
        <>
          <h2 style={{ marginRight: 'auto' }} children={lang.claimsPaymentHistory} />
          <Box class={ThemeWrapperStack}>
            {isMobile && <ClaimJobPaymentFilters />}
            <ClaimJobPaymentActions key="actions" />,
          </Box>
        </>
      }
      subHeader={isDesktop && <ClaimJobPaymentFilters />}
      render={
        <table key="list" className={ThemeTableSimple}>
          <thead>
            <tr>
              <th>{lang.claimNumber}</th>
              <th>{lang.referenceNumber}</th>
              <th>{lang.paymentMethod}</th>
              <th>{lang.payeeName}</th>
              <th>{lang.paymentAt}</th>
              <th>{lang.gstHstPaid}</th>
              <th>{lang.pstPaid}</th>
              <th>{lang.paidAmount}</th>
              <th data-actions>{lang.actions}</th>
            </tr>
          </thead>
          <tbody>
            <DataMap
              data={listApi.payload?.data}
              render={({ data: claimJobPayment }) => {
                const paymentMethodCode =
                  claimJobPayment?.paymentMethodCode ?? claimJobPayment.claimJobPaymentItems[0].paymentMethodCode;
                const allPaymentsSentViaUsBank = claimJobPayment?.claimJobPaymentItems?.every(
                  (it) => it.isPaymentSentViaUsBank
                );
                const allPaymentsSentManually = claimJobPayment?.claimJobPaymentItems?.every(
                  (it) => it.isPaymentSentManually
                );
                const anyPaymentSentViaUsBank = claimJobPayment?.claimJobPaymentItems?.some(
                  (it) => it.isPaymentSentViaUsBank
                );
                const anyPaymentSentManually = claimJobPayment?.claimJobPaymentItems?.some(
                  (it) => it.isPaymentSentManually
                );
                const isCancelled =
                  claimJobPayment?.isCancelled || claimJobPayment?.claimJobPaymentItems?.some((it) => it.isCancelled);

                const isCancellationDisabled = !permissions?.CANCEL_CLAIMJOBPAYMENT || isCancelled;

                return (
                  <React.Fragment key={claimJobPayment.id}>
                    <tr style={{ borderTop: `2px solid ${Theme.Color.shadow}` }}>
                      <td>
                        <b>
                          {
                            claimJobPayment.claimJobPaymentItems.find(
                              (c) => c.claimJobPayment.id === claimJobPayment.id
                            )?.claimJob?.claim?.number
                          }
                        </b>
                      </td>
                      <td>
                        <b>{claimJobPayment.referenceNumber}</b>
                      </td>
                      <td>
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: Theme.Size.XS,
                          }}
                        >
                          <span>
                            {allPaymentsSentManually && (
                              <Box
                                class={ThemeTagRounded}
                                style={{ color: Theme.Color.success, backgroundColor: Theme.Color.successLight }}
                              >
                                <Icon class="fas-check" />
                                &nbsp; {lang.paymentSentManually}
                              </Box>
                            )}
                            {allPaymentsSentViaUsBank && (
                              <Box
                                class={ThemeTagRounded}
                                style={{ color: Theme.Color.success, backgroundColor: Theme.Color.successLight }}
                              >
                                <Icon class="fas-check" />
                                &nbsp; {lang.paymentSentViaUsBank}
                              </Box>
                            )}
                            {anyPaymentSentManually && anyPaymentSentViaUsBank && (
                              <>
                                <Box
                                  class={ThemeTagRounded}
                                  style={{ color: Theme.Color.success, backgroundColor: Theme.Color.successLight }}
                                >
                                  <Icon class="fas-check" />
                                  &nbsp; {lang.paymentSentManually}
                                </Box>
                                <Box
                                  class={ThemeTagRounded}
                                  style={{ color: Theme.Color.success, backgroundColor: Theme.Color.successLight }}
                                >
                                  <Icon class="fas-check" />
                                  &nbsp; {lang.paymentSentViaUsBank}
                                </Box>
                              </>
                            )}
                            {isCancelled && (
                              <Box
                                class={ThemeTagRounded}
                                style={{ color: Theme.Color.error, backgroundColor: Theme.Color.errorLight }}
                              >
                                <Icon class="fas-check" />
                                &nbsp; {lang.cancelled}
                              </Box>
                            )}
                          </span>
                          <span>{translate(paymentMethodCode)}</span>
                        </div>
                      </td>
                      <td>{claimJobPayment.claimJobPaymentPayee?.displayInfo?.title}</td>
                      <td>
                        {claimJobPayment.owner?.fullName && (
                          <>
                            {claimJobPayment.owner.fullName} {lang.at}
                            <br />
                          </>
                        )}
                        {claimJobPayment.createdAtLocal
                          ? localizeDate(claimJobPayment?.createdAtLocal, defaultDateTimeFormat)
                          : 'N/A'}
                      </td>
                      <td>
                        <b>
                          <FormatValue value={`${(claimJobPayment.gstHstAmountPaid || 0).toFixed(2)}$`} />
                        </b>
                      </td>
                      <td>
                        <b>
                          <FormatValue value={`${(claimJobPayment.provincialSalesTaxAmountPaid || 0).toFixed(2)}$`} />
                        </b>
                      </td>
                      <td>
                        <b>
                          <FormatValue value={`${(claimJobPayment.amountPaid || 0).toFixed(2)}$`} />
                        </b>
                      </td>
                      <td>
                        <Box class={ThemeWrapperStack}>
                          <Button
                            class={ThemeButtonCircle}
                            media={<Icon class="fas-info-circle" />}
                            onClick={pageRouter.updateParams}
                            data={{
                              id: claimJobPayment.id.toString(),
                              action: FinanceClaimJobPaymentInfoTab.INFO,
                            }}
                            htmlElementProps={{ title: lang.info }}
                            active
                          />
                          <LinkButton
                            class={ThemeButtonCircle}
                            url={claimJobPayment.remittanceDocument?.url}
                            media={<Icon class="fas-download" />}
                            target="_blank"
                            disabled={!claimJobPayment?.remittanceDocument?.url}
                            htmlElementProps={{
                              title: !claimJobPayment?.remittanceDocument?.url ? lang.missing : lang.download,
                            }}
                            active
                          />
                          <Button
                            class={ThemeButtonCircle}
                            media={<Icon class="if-ui-close" />}
                            onClick={pageRouter.updateParams}
                            data={{
                              id: claimJobPayment.id.toString(),
                              action: ClaimJobPaymentAction.CANCEL,
                            }}
                            htmlElementProps={{ title: lang.cancelPayment }}
                            disabled={isCancellationDisabled}
                            active
                          />
                          <Button
                            class={ThemeButtonCircle}
                            media={<Icon class="fas-envelope" />}
                            onClick={pageRouter.updateParams}
                            data={{
                              id: claimJobPayment.id.toString(),
                              action: ClaimJobPaymentAction.SEND_EMAIL,
                            }}
                            htmlElementProps={{ title: lang.sendEmail }}
                            active
                          />
                        </Box>
                      </td>
                    </tr>
                  </React.Fragment>
                );
              }}
            />
          </tbody>
        </table>
      }
      footer={
        <Pagination
          class={ThemePaginationRounded}
          count={listApi.payload?.count}
          offset={listApi.body?.offset}
          limit={listApi.body?.limit}
          onLimitChange={(limit) => listApi.execute((body) => ({ ...body, limit, offset: 0 }))}
          onOffsetChange={(offset) => listApi.execute((body) => ({ ...body, offset }))}
        />
      }
    />
  );
}
